import { Component, type ReactNode } from "react";

export type ErrorBoundaryScope = "app" | "page";

interface AppErrorBoundaryProps {
  scope: ErrorBoundaryScope;
  /** Clears the error when it changes, e.g. after navigating to another page. */
  resetKey?: string;
  /** Recovery UI; use `getSupportId(error)` to show a support ID. */
  fallback: (error: unknown, scope: ErrorBoundaryScope) => ReactNode;
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: unknown;
  hasError: boolean;
}

/** Catches render crashes and shows the app's crash screen. Reporting happens in the React root hooks. */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: undefined, hasError: false };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { error, hasError: true };
  }

  componentDidUpdate(previous: AppErrorBoundaryProps) {
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: undefined, hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return this.props.fallback(this.state.error, this.props.scope);
  }
}
